<template>
  <!-- eslint-disable vue/attribute-hyphenation -->
  <div
    ref="container"
    :class="[$style.dropDownFieldContainer, dropDownFieldContainerExtra]"
    tabindex="0"
  >
    <label
      v-if="label"
      :class="$style.label"
    >
      {{ label }}
    </label>
    <IconTooltip
      v-if="tooltip && tooltip.text"
      :iconClass="['jc-info-circle', $style.hoverIcon]"
    >
      <!-- DO NOT ALLOW ANY USER INPUT IN STRING PASSED TO v-html -->
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-html="tooltip.text"
      />
      <!-- eslint-enable vue/no-v-html -->
    </IconTooltip>
    <BDropdown
      ref="dropDownContainer"
      :class="{
        [$style.dropDownContainer]: true,
        [$style.dropDownContainerNoValue]: !value,
        [$style.validationError]: showError,
        [customContainerClass]: customContainerClass,
      }"
      :disabled="disabled"
      :name="name"
      :required="required"
      :text="selectedOptionText || placeholder"
      @hidden="handleHidden"
      @keyup.native="handleKeyup"
      @shown="handleShown"
    >
      <BDropdownHeader
        v-if="headerText"
        :class="$style.dropDownHeader"
      >
        {{ headerText }}
        <slot name="headerLink" />
      </BDropdownHeader>
      <BDropdownDivider
        v-if="headerText"
        :class="$style.dropDownDivider"
      />
      <BDropdownItem
        v-for="(option, i) in fullOptions"
        :key="i"
        :class="getDropDownMenuItemClass(option)"
        :disabled="option.disabled"
        @click="handleMenuItemClick(option)"
      >
        {{ getLabel(option) }}
      </BDropdownItem>
    </BDropdown>
    <div
      v-if="clearable && value"
      :class="$style.dropDownCloseButton"
      @click="handleMenuRemoveSelection()"
    >
      <Icon
        icon="close"
        :scale="0.75"
      />
    </div>

    <div :class="[$style.error, errorStyleExceptions]">
      <transition
        :enter-active-class="$style.fadeEnterActive"
        :enter-class="$style.fadeEnter"
        :leave-active-class="$style.fadeLeaveActive"
        :leave-to-class="$style.fadeLeaveTo"
        name="fade"
      >
        <span v-if="showError">{{ handleError() }}</span>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  BDropdown,
  BDropdownDivider,
  BDropdownHeader,
  BDropdownItem,
} from 'bootstrap-vue';
import { Icon } from '@jumpcloud/ui-components';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import OptionalValidationMixin from 'client/components/FormValidation/mixins/OptionalValidationMixin';

export default {
  name: 'DropDown',

  // Support vee-validate on DropDown custom element
  // SEE: https://vee-validate.logaretm.com/v2/examples/custom-component.html
  $_veeValidate: {
    value() {
      return this.value;
    },
    name() {
      return this.name;
    },
  },

  inject: ['$validator'],

  components: {
    BDropdown,
    BDropdownDivider,
    BDropdownHeader,
    BDropdownItem,
    IconTooltip,
    Icon,
  },

  mixins: [
    OptionalValidationMixin,
  ],

  props: {
    attribute: {
      type: String,
      default: '',
    },
    clearable: Boolean,
    customContainerClass: {
      type: String,
      default: '',
    },
    dropDownFieldContainerExtra: {
      type: String,
      default: '',
    },
    emptyOptionText: {
      type: String,
      default: '',
    },
    headerText: {
      type: String,
      default: '',
    },
    includeEmptyOption: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    optionLabelKey: {
      type: String,
      default: '',
    },
    optionValueKey: {
      type: String,
      default: '',
    },
    /**
     * options can be provided as a simple array: ['apple', 'banana']
     * or as an array of objects: [{ name: 'apple', value: 'a' }, { name: 'banana', value: 'b' }]
     */
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    tooltip: {
      type: Object,
      default: () => {},
    },
    value: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    handleFocus: null,
    pressedKeys: '',
  }),

  computed: {
    fullOptions() {
      const fullOptions = this.options.slice(0);
      if (this.includeEmptyOption) {
        let emptyOption = this.emptyOptionText;
        if (this.optionLabelKey && this.optionValueKey) {
          emptyOption = {
            [this.optionLabelKey]: this.emptyOptionText,
            [this.optionValueKey]: '',
          };
        }
        fullOptions.unshift(emptyOption);
      }
      return fullOptions;
    },

    selectedOptionText() {
      const selectedOption = this.options.find(option => this.getValue(option) === this.value);
      if (!selectedOption) {
        return this.value || '';
      }

      return this.getLabel(selectedOption);
    },

    showError() {
      return this.v2Errors && this.v2Errors.has(this.name);
    },
  },

  watch: {
    async value(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.validate();
      }
    },
  },

  mounted() {
    this.handleFocus = () => {
      this.$validator.pause();
      this.$refs.dropDownContainer.visible = true;
    };

    if (this.$refs.container) {
      this.$refs.container.addEventListener('focus', this.handleFocus);
    }
  },

  beforeDestroy() {
    if (this.$refs.container) {
      this.$refs.container.removeEventListener('focus', this.handleFocus);
    }
  },

  methods: {
    getDropDownMenuItemClass(option) {
      const classes = [this.$style.dropDownMenuItem];
      if (this.getLabel(option) === this.selectedOptionText) {
        classes.push(this.$style.dropDownMenuItemActive);
      }
      if (option.disabled) {
        classes.push(this.$style.dropDownMenuItemDisabled);
      }
      return classes;
    },

    getLabel(option) {
      return this.optionLabelKey ? option[this.optionLabelKey] : option;
    },

    getValue(option) {
      return this.optionValueKey ? option[this.optionValueKey] : option;
    },

    async handleHidden() {
      this.pressedKeys = '';
      await this.validate();
      this.$emit('hidden', { key: this.attribute, value: this.value, name: this.name });
    },

    handleKeyup(event) {
      /**
       * TODO: This only works when the dropdown menu is already open.
       * When a user types a letter, it should jump to the first option starting
       * with that letter or the letters that have already been typed.
       */
      const key = event.key.toLowerCase();
      const dropDownMenu = this.$refs.dropDownContainer.$el.children[1];
      const dropDownMenuY = dropDownMenu.getBoundingClientRect().y;

      // Ignore keys that should not jump to options in the drop down menu.
      if (key.length > 1) {
        return;
      }

      this.pressedKeys = `${this.pressedKeys}${key}`;

      let closestOptionIndex = this.options.findIndex((option) => {
        const label = this.getLabel(option);
        return label.toLowerCase().indexOf(this.pressedKeys.toLowerCase()) === 0;
      });

      // If a match wasn't found then reset the pressed keys and jump to the
      // top of the list.
      if (closestOptionIndex === -1) {
        this.pressedKeys = '';
        closestOptionIndex = 0;
      }

      if (this.includeEmptyOption) {
        closestOptionIndex += 1;
      }

      const closestOption = dropDownMenu.children[closestOptionIndex];
      const closestOptionY = closestOption.getBoundingClientRect().y;

      dropDownMenu.scrollTop += closestOptionY - dropDownMenuY - 8;
    },

    handleMenuItemClick(option) {
      const value = this.getValue(option);
      this.$emit('input', value, this.attribute, this.name);
      this.pressedKeys = '';
    },

    handleMenuRemoveSelection() {
      this.$emit('input', '', this.attribute, this.name);
      this.pressedKeys = '';
    },

    handleShown() {
      this.$validator.resume();
    },

    /**
     * Only perform validation when requested by the parent. We check the field bag to
     * see if our field is present. If not, then validation was not requested and should not
     * be performed now.
     */
    async validate() {
      if (this.$validator.fields.find({ name: this.name })) {
        await this.$validator.validate(this.name);
      }
    },
  },
};
</script>

<style module>
@import './css/FormValidationFields.css';
@import './css/formFields.css';

@value (panelSpeed) from '~css/transitions.css';
@value border-width 2px;

.label {
  composes: label from "~components/FormValidation/css/formFields.css";
  composes: label-color from "~components/FormValidation/css/formFields.css";
  composes: label-font from "~components/FormValidation/css/formFields.css";
  composes: label-position from "~components/FormValidation/css/formFields.css";
  composes: label-typography from "~components/FormValidation/css/formFields.css";
}

.dropDownFieldContainer {
  display: block;
  margin-top: 5px;
  outline: none;
  width: 100%;
}

.dropDownContainer {
  border: border-width solid var(--jcGrey200);
  border-radius: border-width;
  outline: none;
  transition: border-color panelSpeed ease-in-out;
}

.dropDownContainer:focus-within {
  border: border-width solid var(--jcPrimary);
  border-radius: border-width;
}

.dropDownContainer.validationError,
.dropDownContainer.validationError:focus {
  border: border-width solid var(--jcError);
  border-radius: border-width;
}

.dropDownContainer button {
  background-color: var(--jcWhite300);
  border: 0;
  color: var(--jc-text-color);
  font-weight: 400;
  height: 30px;
  outline: none;
  overflow: hidden;
  padding: 5px 10px;
  text-align: left;
  text-overflow: ellipsis;
  transition: border-color panelSpeed ease-in-out;
  width: 100%;
}

.dropDownContainerNoValue button {
  color: var(--jc-text-color);
}

.dropDownContainer button::after {
  right: 10px;
}

.dropDownContainer button:active {
  box-shadow: none;
}

.dropDownContainer button:hover {
  background: var(--jcGrey50);
}

.dropDownContainer button:focus,
.dropDownContainer:focus-within button {
  background-color: var(--jcPrimary50);
}

.dropDownContainer ul,
.dropDownContainer button:active:focus {
  outline: none !important;
}

.dropDownContainer .dropDownDivider {
  margin: 7px 0;
}

.dropDownDivider hr {
  margin: 0;
}

.dropDownContainer .dropDownHeader {
  padding: 3px 20px;
}

.dropDownHeader :global(.dropdown-header) {
  padding: 0;
}

.hoverIcon {
  font-size: fontHeight;
}

.dropDownCloseButton {
  cursor: pointer;
  float: right;
  margin-right: var(--jc-spacer-medium);
  margin-top: var(--jc-spacer-negative-medium);
  position: relative;
  width: 20px;
}
</style>
